import React from "react";
import { RefreshCw, Database, Download, Smartphone, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SyncSettings } from "../layout";
import { FamilyManagement } from "./FamilyManagement";
import { ActivityHistory } from "./ActivityHistory";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useStarData } from "@/hooks/useStarData";
import { useFirebase } from "@/providers/FirebaseProvider";
import { useStarUI } from "@/hooks/useStarUI";
import { usePWA } from "@/hooks/usePWA";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { WalletSettings, TabSettings } from "@/utils/storage";

interface AdminTabProps {
  onShowResetDialog: () => void;
  onDataSync?: () => void;
  adsEnabled: boolean;
  onAdsEnabledChange: (enabled: boolean) => void;
  walletSettings?: WalletSettings;
  onWalletSettingsChange?: (settings: WalletSettings) => void;
  bottomNavigation?: boolean;
  onBottomNavigationChange?: (enabled: boolean) => void;
  tabSettings?: TabSettings;
  onTabSettingsChange?: (settings: TabSettings) => void;
}

const AdminTab: React.FC<AdminTabProps> = ({
  onShowResetDialog,
  onDataSync,
  adsEnabled,
  onAdsEnabledChange,
  walletSettings,
  onWalletSettingsChange,
  bottomNavigation = false,
  onBottomNavigationChange,
  tabSettings,
  onTabSettingsChange
}) => {
  const { editMode } = useStarUI();
  const { data } = useStarData();
  const { user } = useFirebase();

  const handleDownloadData = () => {
    if (!data) return;
    
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `star-chores-data-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  };

  // Local state for immediate feedback
  const [localBottomNavigation, setLocalBottomNavigation] =
    React.useState(bottomNavigation);
  const [localConversionRate, setLocalConversionRate] = React.useState(
    walletSettings?.conversionRate || 10
  );
  const [localTabSettings, setLocalTabSettings] = React.useState<TabSettings>(
    tabSettings || {
      tasksEnabled: true,
      rewardsEnabled: true,
      trophiesEnabled: true,
      wishListEnabled: false,
      walletEnabled: true,
    }
  );

  // PWA state (shared hook keeps install/connectivity status consistent app-wide)
  const { isInstalled: isPWA, isInstallable, isOnline, promptInstall } = usePWA();

  const handleInstallPWA = async () => {
    await promptInstall();
  };

  // Sync local state with props when they change
  React.useEffect(() => {
    setLocalBottomNavigation(bottomNavigation);
  }, [bottomNavigation]);

  React.useEffect(() => {
    setLocalConversionRate(walletSettings?.conversionRate || 10);
  }, [walletSettings?.conversionRate]);

  React.useEffect(() => {
    setLocalTabSettings(
      tabSettings || {
        tasksEnabled: true,
        rewardsEnabled: true,
        trophiesEnabled: true,
        wishListEnabled: false,
        walletEnabled: true,
      }
    );
  }, [tabSettings]);

  const handleBottomNavigationChange = (enabled: boolean) => {
    // Update local state immediately for visual feedback
    setLocalBottomNavigation(enabled);

    // Update parent state
    if (onBottomNavigationChange) {
      onBottomNavigationChange(enabled);
    }
  };

  const handleConversionRateChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;

    // Update local state immediately for visual feedback
    if (value === "" || value === "0") {
      setLocalConversionRate(1); // Minimum value
    } else {
      const rate = parseInt(value);
      if (!isNaN(rate) && rate > 0) {
        setLocalConversionRate(rate);
      }
    }

    // Update parent state with validation
    // Enable wallet feature when wallet tab is enabled and conversion rate is set
    const rate = parseInt(value);
    if (walletSettings && onWalletSettingsChange && !isNaN(rate) && rate > 0) {
      onWalletSettingsChange({
        enabled: localTabSettings.walletEnabled, // Enable based on tab visibility
        conversionRate: rate,
      });
    }
  };

  const handleTabSettingChange = (
    tabName: keyof TabSettings,
    enabled: boolean
  ) => {
    // Update local state immediately for visual feedback
    const newTabSettings = {
      ...localTabSettings,
      [tabName]: enabled,
    };

    setLocalTabSettings(newTabSettings);

    // Update parent state
    if (onTabSettingsChange) {
      onTabSettingsChange(newTabSettings);
    }

  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Admin Panel</CardTitle>
          <CardDescription>Manage your app settings and data</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="font-medium mb-3">Data Synchronization</h3>
            <SyncSettings onDataSync={onDataSync} />
          </div>

          <Separator />

          <div>
            <h3 className="font-medium mb-3">Advertisements</h3>
            <div className="flex items-center justify-between rounded-lg border p-4 dark:border-gray-700">
              <div className="space-y-0.5">
                <label
                  htmlFor="ads-toggle"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Show Advertisements
                </label>
                <p className="text-xs text-muted-foreground">
                  Toggle to show or hide ads in the app.
                </p>
              </div>
              <Switch
                id="ads-toggle"
                checked={adsEnabled}
                onCheckedChange={onAdsEnabledChange}
                className="peer"
              />
            </div>
          </div>

          <Separator />

          <div>
            <h3 className="font-medium mb-3">Navigation Layout</h3>
            <div className="flex items-center justify-between rounded-lg border p-4 dark:border-gray-700">
              <div className="space-y-0.5">
                <label
                  htmlFor="bottom-nav-toggle"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Enable bottom tab navigation
                </label>
                <p className="text-xs text-muted-foreground">
                  Move navigation tabs to the bottom of the screen with icons.
                </p>
              </div>
              <Switch
                id="bottom-nav-toggle"
                checked={localBottomNavigation}
                onCheckedChange={handleBottomNavigationChange}
                className="peer"
              />
            </div>
          </div>

          <Separator />

          <div>
            <h3 className="font-medium mb-3">Tab Visibility</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-4 dark:border-gray-700">
                <div className="space-y-0.5">
                  <label
                    htmlFor="tasks-tab-toggle"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Enable Tasks Tab
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Show the Tasks tab. <strong>Warning:</strong> Disabling may
                    limit core functionality.
                  </p>
                </div>
                <Switch
                  id="tasks-tab-toggle"
                  checked={localTabSettings.tasksEnabled}
                  onCheckedChange={(enabled) =>
                    handleTabSettingChange("tasksEnabled", enabled)
                  }
                  className="peer"
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4 dark:border-gray-700">
                <div className="space-y-0.5">
                  <label
                    htmlFor="rewards-tab-toggle"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Enable Rewards Tab
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Show the Rewards tab.
                  </p>
                </div>
                <Switch
                  id="rewards-tab-toggle"
                  checked={localTabSettings.rewardsEnabled}
                  onCheckedChange={(enabled) =>
                    handleTabSettingChange("rewardsEnabled", enabled)
                  }
                  className="peer"
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4 dark:border-gray-700">
                <div className="space-y-0.5">
                  <label
                    htmlFor="trophies-tab-toggle"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Enable Trophies Tab
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Show the Trophies/Achievements tab.
                  </p>
                </div>
                <Switch
                  id="trophies-tab-toggle"
                  checked={localTabSettings.trophiesEnabled}
                  onCheckedChange={(enabled) =>
                    handleTabSettingChange("trophiesEnabled", enabled)
                  }
                  className="peer"
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4 dark:border-gray-700">
                <div className="space-y-0.5">
                  <label
                    htmlFor="wishlist-tab-toggle"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Enable Wish List Tab
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Show the Wish List tab for managing desired items.
                  </p>
                </div>
                <Switch
                  id="wishlist-tab-toggle"
                  checked={localTabSettings.wishListEnabled}
                  onCheckedChange={(enabled) =>
                    handleTabSettingChange("wishListEnabled", enabled)
                  }
                  className="peer"
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4 dark:border-gray-700">
                <div className="space-y-0.5">
                  <label
                    htmlFor="wallet-tab-toggle"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Enable Wallet Tab
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Show the Wallet tab for money rewards (requires wallet feature enabled).
                  </p>
                </div>
                <Switch
                  id="wallet-tab-toggle"
                  checked={localTabSettings.walletEnabled}
                  onCheckedChange={(enabled) =>
                    handleTabSettingChange("walletEnabled", enabled)
                  }
                  className="peer"
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4 dark:border-gray-700">
                <div className="space-y-0.5">
                  <label
                    htmlFor="allow-add-money-toggle"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Allow Adding Money
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Allow adding money sources from the main Wallet page (not just edit mode).
                  </p>
                </div>
                <Switch
                  id="allow-add-money-toggle"
                  checked={localTabSettings.allowAddMoney || false}
                  onCheckedChange={(enabled) =>
                    handleTabSettingChange("allowAddMoney", enabled)
                  }
                  className="peer"
                />
              </div>

              {localTabSettings.walletEnabled && (
                <div className="rounded-lg border p-4 dark:border-gray-700 bg-muted/30">
                  <Label
                    htmlFor="conversion-rate"
                    className="text-sm font-medium"
                  >
                    Star to Dollar Conversion Rate
                  </Label>
                  <div className="flex items-center space-x-2 mt-2">
                    <Input
                      id="conversion-rate"
                      type="number"
                      min="1"
                      value={localConversionRate}
                      onChange={handleConversionRateChange}
                      className="w-20"
                    />
                    <span className="text-sm text-muted-foreground">
                      stars = $1.00
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Example: 10 stars = $1.00, 20 stars = $2.00
                  </p>
                </div>
              )}
            </div>
          </div>

          <Separator />

          <div>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="pwa-settings">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Smartphone className="h-4 w-4" />
                    <span className="font-medium">Progressive Web App (PWA)</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    <div className="rounded-lg border p-4 dark:border-gray-700 bg-muted/30">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Status:</span>
                            <span className={`text-sm ${isPWA ? 'text-green-600' : 'text-muted-foreground'}`}>
                              {isPWA ? '✓ Running as PWA' : 'Running in browser'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Connection:</span>
                            <span className={`flex items-center gap-1 text-sm ${isOnline ? 'text-green-600' : 'text-orange-600'}`}>
                              {isOnline ? (
                                <>
                                  <Wifi className="h-3 w-3" />
                                  Online
                                </>
                              ) : (
                                <>
                                  <WifiOff className="h-3 w-3" />
                                  Offline
                                </>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {!isPWA && (
                      <div className="space-y-3">
                        <div className="rounded-lg border p-4 dark:border-gray-700">
                          <h4 className="text-sm font-medium mb-2">Install as App</h4>
                          <p className="text-xs text-muted-foreground mb-3">
                            Install Star Chore Adventure on your device for a better experience:
                          </p>
                          <ul className="text-xs text-muted-foreground space-y-1 mb-3 list-disc list-inside">
                            <li>Works offline</li>
                            <li>Faster loading</li>
                            <li>No browser UI</li>
                            <li>Home screen icon</li>
                          </ul>

                          {isInstallable ? (
                            <Button
                              size="sm"
                              className="w-full"
                              onClick={handleInstallPWA}
                            >
                              <Smartphone className="mr-2 h-4 w-4" />
                              Install App
                            </Button>
                          ) : (
                            <div className="text-xs text-muted-foreground">
                              <p className="mb-2">To install manually:</p>
                              <ul className="list-disc list-inside space-y-1 ml-2">
                                <li><strong>iOS Safari:</strong> Tap Share → Add to Home Screen</li>
                                <li><strong>Android Chrome:</strong> Tap Menu (⋮) → Install app</li>
                                <li><strong>Desktop:</strong> Look for install icon in address bar</li>
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {isPWA && (
                      <div className="rounded-lg border p-4 dark:border-gray-700 bg-green-50 dark:bg-green-950/20">
                        <div className="flex items-start gap-2">
                          <div className="text-green-600 mt-0.5">✓</div>
                          <div>
                            <h4 className="text-sm font-medium text-green-900 dark:text-green-100 mb-1">
                              App Installed Successfully
                            </h4>
                            <p className="text-xs text-green-700 dark:text-green-300">
                              You're using Star Chore Adventure as a Progressive Web App.
                              Enjoy offline support and faster performance!
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <Separator />

          <div>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="data-debug">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    <span className="font-medium">Data Debug Info</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    {data ? (
                      <>
                        <div className="space-y-2 rounded-lg border p-4 dark:border-gray-700 bg-muted/30">
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="font-medium">Current Stars:</div>
                            <div className="font-mono">{data.stars || 0}</div>

                            <div className="font-medium">Total Stars Earned:</div>
                            <div className="font-mono">{data.totalStarsEarned || 0}</div>

                            <div className="font-medium">Wallet Balance (stored):</div>
                            <div className="font-mono">${(data.walletData?.currentBalance || 0).toFixed(2)}</div>

                            <div className="font-medium">Wallet Lifetime Earned:</div>
                            <div className="font-mono">${(data.walletData?.lifetimeEarned || 0).toFixed(2)}</div>

                            <div className="font-medium">Conversion Rate:</div>
                            <div className="font-mono">{walletSettings?.conversionRate || 10} stars = $1</div>

                            <div className="font-medium text-green-600">Balance (from current stars):</div>
                            <div className="font-mono text-green-600 font-bold">
                              ${((data.stars || 0) / (walletSettings?.conversionRate || 10)).toFixed(2)}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-3">
                            The green value shows what the wallet balance should display (based on current stars).
                          </p>
                        </div>

                        <div className="rounded-lg border p-4 dark:border-gray-700">
                          <h4 className="text-sm font-medium mb-2">Raw Firebase Data</h4>
                          <div className="max-h-[300px] overflow-y-auto rounded-md bg-muted p-3">
                            <pre className="text-xs">
                              <code>
                                {JSON.stringify(data, null, 2)}
                              </code>
                            </pre>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full mt-3"
                            onClick={handleDownloadData}
                          >
                            <Download className="mr-2 h-4 w-4" />
                            Export Data
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="rounded-lg border p-4 dark:border-gray-700 bg-muted/30">
                        <p className="text-sm text-muted-foreground">Loading data...</p>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <Separator />

          <div>
            <h3 className="font-medium mb-3 text-red-500">Danger Zone</h3>
            <div className="space-y-3">
              <Button
                variant="outline"
                size="sm"
                onClick={onShowResetDialog}
                className="w-full flex items-center justify-center text-red-500 hover:text-red-700 hover:border-red-200"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Reset All Data
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Family Sharing Management */}
      {user && <FamilyManagement />}

      {/* Activity History */}
      {user && data && (
        <ActivityHistory
          activityLog={data.activityLog}
          familyId={data.familyId}
        />
      )}
    </div>
  );
};

export default AdminTab;
